<template>
    <section
        ref="section"
        class="ressources-bloc-section"
        :class="{'ressources-bloc-section--hide-in-mobile': slice.primary.hideInMobile}"
        aria-label="resources"
    >
        <PrismicRichText :field="slice.primary.title" class="title" />
        <div class="ressources-bloc-section__swiper">
            <NuxtErrorBoundary>
                <Swiper :slides-per-view="1" :loop="slice.items.length > 1" :space-between="20" :modules="modules">
                    <SwiperSlide v-for="(item, i) in slice.items" :key="`slice-item-${i}`">
                        <div class="card">
                            <div class="top">
                                <MktMaltPrismicImage
                                    v-if="item.thumbnail"
                                    :image-field="item.thumbnail"
                                    class="thumbnail"
                                    sizes="small:309px mobile:874 wide:294"
                                />
                                <div v-if="slice.variation === 'default'" class="tags">
                                    {{ getItemTags(item) }}
                                </div>

                                <PrismicRichText :field="item.title" class="cart-title" />
                                <PrismicRichText :field="item.subtitle" class="cart-subtitle" />
                            </div>
                            <ServerSideNavigationPrismicLink
                                v-if="isFilledKeyText(item.loggedInLinkText) && isFilledKeyText(item.loggedInLink) && authStore.loggedIn"
                                :field="item.loggedInLink"
                                class="link-button"
                                data-component-source="resources-bloc"
                            >
                                {{ item.loggedInLinkText }}
                            </ServerSideNavigationPrismicLink>
                            <ServerSideNavigationPrismicLink
                                v-else-if="isFilledKeyText(item.linkText) && isFilledKeyText(item.link)"
                                :field="item.link"
                                class="link-button"
                                data-component-source="resources-bloc"
                            >
                                {{ item.linkText }}
                            </ServerSideNavigationPrismicLink>
                            <div v-if="isDefaultItem(item)" class="info">
                                <span class="date">
                                    {{
                                        item.date &&
                                        new Date(item.date).toLocaleDateString(undefined, {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                        })
                                    }}
                                </span>
                                <span>{{ item.duration }}</span>
                            </div>
                        </div>
                    </SwiperSlide>
                    <SliderControls
                        :prev-arrow-label="slice.primary.prevArrowLabel ?? 'prev'"
                        :next-arrow-label="slice.primary.nextArrowLabel ?? 'next'"
                    />
                </Swiper>
            </NuxtErrorBoundary>
        </div>
        <div
            :class="{
                cards: true,
                'two-columns': slice.items.length === 2,
            }"
        >
            <div v-for="(item, i) in slice.items" :key="`slice-item-${i}`" class="card">
                <div class="top">
                    <MktMaltPrismicImage
                        v-if="item.thumbnail"
                        :image-field="item.thumbnail"
                        class="thumbnail"
                        :sizes="slice.items.length > 2 ? 'small:309px mobile:874 wide:294' : 'small:309px mobile:874 wide:492'"
                    />
                    <div v-if="isDefaultItem(item)" class="tags">
                        {{ item.tags }}
                    </div>

                    <PrismicRichText :field="item.title" class="cart-title" />
                    <PrismicRichText :field="item.subtitle" class="cart-subtitle" />
                </div>
                <ServerSideNavigationPrismicLink
                    v-if="isFilledKeyText(item.loggedInLinkText) && isFilledKeyText(item.loggedInLink) && authStore.loggedIn"
                    :field="item.loggedInLink"
                    class="link-button"
                    data-component-source="resources-bloc"
                >
                    {{ item.loggedInLinkText }}
                </ServerSideNavigationPrismicLink>
                <ServerSideNavigationPrismicLink
                    v-else-if="isFilledKeyText(item.linkText) && isFilledKeyText(item.link)"
                    :field="item.link"
                    class="link-button"
                    data-component-source="resources-bloc"
                >
                    {{ item.linkText }}
                </ServerSideNavigationPrismicLink>
                <div v-if="isDefaultItem(item)" class="info">
                    <span class="date">
                        {{
                            item.date &&
                            new Date(item.date).toLocaleDateString(undefined, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                            })
                        }}
                    </span>
                    <span>{{ item.duration }}</span>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {onMounted, useTemplateRef} from 'vue';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ResourcesBlocSlice, ResourcesBlocSliceDefaultItem, ResourcesBlocSliceStandardItem, Simplify} from '@prismicio-types';
    import {A11y} from 'swiper/modules';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import {MktMaltPrismicImage} from '#components';
    import {usePrismicTypeGuards, useComponentTracking, useAuth} from '#imports';
    import ServerSideNavigationPrismicLink from '~/components/ServerSideNavigationPrismicLink.vue';
    import 'swiper/css';
    import SliderControls from '~/components/SliderControls.vue';
    import errorHelpers from '~/composables/errorHelpers';

    const authStore = useAuth();
    const props = defineProps(getSliceComponentProps<ResourcesBlocSlice>());
    const {isFilledKeyText} = usePrismicTypeGuards();
    const modules = [A11y];
    const section = useTemplateRef('section');

    function getItemTags(item: Simplify<ResourcesBlocSliceDefaultItem> | Simplify<ResourcesBlocSliceStandardItem>) {
        return (item as Simplify<ResourcesBlocSliceDefaultItem>).tags;
    }
    function isDefaultItem(item: any): item is Simplify<ResourcesBlocSliceDefaultItem> {
        return item.date || item.duration || item.tags;
    }
    onMounted(() => {
        const {throwIfItemsCountIsOutOfBound, throwIfMissingItemsProperties, getPreviewErrorHandler} = errorHelpers();
        const errorHandler = getPreviewErrorHandler();

        if (errorHandler.enabled) {
            const itemsCount = props.slice.items.length;
            throwIfItemsCountIsOutOfBound(errorHandler.toast, itemsCount, 'cards', 2, 9);
            throwIfMissingItemsProperties(
                errorHandler.toast,
                props.slice.items,
                [
                    {name: 'thumbnail', type: 'image'},
                    {name: 'link', type: 'rawText'},
                    {name: 'linkText', type: 'rawText'},
                    {name: 'title', type: 'field'},
                    {name: 'subtitle', type: 'field'},
                ],
                'cards',
            );
            if (props.slice.variation === 'default') {
                throwIfMissingItemsProperties(
                    errorHandler.toast,
                    props.slice.items,
                    [
                        {name: 'tags', type: 'rawText'},
                        {name: 'date', type: 'date'},
                        {name: 'duration', type: 'rawText'},
                    ],
                    'cards',
                );
            }
        }
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .ressources-bloc-section {
        background-color: var(--joy-color-background-default);
        color: var(--joy-color-neutral-60);
        display: flex;
        flex-direction: column;
        align-items: center;
        font-family: var(--joy-font-family-base);

        @include content-width;

        max-width: 100vw;

        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: flex;
            }
        }

        &__swiper {
            display: block;
            width: 100%;

            @media (min-width: $mobile-breakpoint) {
                display: none;
            }
        }

        .title h2 {
            margin-bottom: 60px;
            position: relative;
            text-align: center;

            @include secondary-title;

            em {
                font-style: normal;
                background-color: var(--joy-color-quaternary-50);
                border-radius: var(--joy-core-radius-5);
                padding: 0 10px;
                margin: 0 -10px;
            }

            @media (max-width: $mobile-breakpoint) {
                margin-bottom: 22px;
            }
        }

        .cards {
            display: none;
            grid-template-columns: 1fr 1fr 1fr;
            gap: var(--joy-core-spacing-9) var(--joy-core-spacing-9);
            max-width: $container-max-width;
            width: 100%;

            @media (min-width: $mobile-breakpoint) {
                display: grid;
            }

            &.two-columns {
                grid-template-columns: 1fr 1fr;

                .thumbnail {
                    width: 536px;
                    height: auto;
                    max-width: 100%;
                }
            }
        }

        .swiper {
            width: 100%;

            .swiper-slide {
                width: 100%;
                box-sizing: border-box;
                padding: 20px;
            }
        }

        .card {
            box-sizing: border-box;
            width: 100%;
            padding: 32px;
            text-align: left;
            justify-items: space-between;

            @include flex-column-start;
            @include card;

            @media (max-width: $mobile-breakpoint) {
                width: unset;
            }

            .top {
                flex-grow: 1;
                width: 100%;
            }

            .thumbnail {
                width: 100%;
                height: auto;
                margin-bottom: 32px;
                border-radius: var(--joy-core-radius-4);
                aspect-ratio: 16 / 9;
                object-fit: cover;
            }

            .tags {
                color: var(--joy-color-secondary-30);
                text-transform: uppercase;
                font-size: var(--joy-font-size-primary-200);
                font-weight: var(--joy-font-weight-bold);
                margin-bottom: 16px;

                @media (max-width: $mobile-breakpoint) {
                    margin-bottom: var(--joy-core-spacing-3);
                }
            }

            .cart-title {
                margin-bottom: 16px;

                h3 {
                    @include tertiary-title;

                    font-size: var(--joy-font-size-primary-600);
                    font-weight: var(--joy-font-weight-bold);
                }

                @media (max-width: $mobile-breakpoint) {
                    margin-bottom: var(--joy-core-spacing-3);
                }
            }

            .cart-subtitle {
                margin-bottom: 24px;
                line-height: var(--joy-line-height-large);

                @media (max-width: $mobile-breakpoint) {
                    margin-bottom: 32px;
                }
            }

            .link-button {
                @include link;
            }

            .info {
                color: var(--joy-color-text-default);
                font-size: var(--joy-font-size-primary-300);
                margin-top: 32px;

                .date {
                    padding-right: var(--joy-core-spacing-3);
                    margin-right: var(--joy-core-spacing-3);
                    border-right: 1px solid var(--joy-color-neutral-30);
                }
            }
        }
    }
</style>
